/**
 * The one answer type in v2.
 *
 * Every dynamic question is answered on the same symmetric scale from -3 to
 * +3, where the sign carries the meaning and the magnitude carries the
 * strength:
 *
 *     -3  hate it        0  don't care        +3  love it
 *
 * That uniformity is what makes the whole thing composable. Questions can be
 * added, renamed and regrouped from /admin without a line of scoring code
 * changing, because the scorer never needs to know what a question is about —
 * only that two people put their sliders somewhere.
 *
 * Alongside the answer, a user may say how much this particular question
 * should count for them: -1 (not very important), 0 (regular), +1 (very
 * important).
 */

export const SCALE_MIN = -3;
export const SCALE_MAX = 3;
/** Widest possible gap between two answers — the last entry of the table below. */
export const SCALE_SPAN = SCALE_MAX - SCALE_MIN; // 6

export type ScaleValue = -3 | -2 | -1 | 0 | 1 | 2 | 3;
export type Importance = -1 | 0 | 1;

export const SCALE_VALUES: ScaleValue[] = [-3, -2, -1, 0, 1, 2, 3];

/**
 * What an unset slider says. Not the same as answering 0, which is a real
 * answer meaning "this doesn't move me".
 */
export const UNANSWERED_LABEL = "Not answered";

// The words for the seven positions themselves live in ./ladders, because they
// belong to the question rather than to the scale: what -3 *is* never changes,
// what it should be called depends on whether the row is about spicy food or
// how often you pray.

export const IMPORTANCE_LABELS: Record<Importance, string> = {
  [-1]: "Not very important",
  0: "Normal importance",
  1: "Very important",
};

/**
 * How much a stated importance multiplies a question's weight.
 *
 * The three values are deliberately a geometric series around 1 (1/2.5, 1,
 * 2.5). Combined with the geometric mean below, that gives the tidy property
 * that one person's "not very important" exactly cancels the other's "very
 * important" and the question lands back at normal weight.
 */
export const IMPORTANCE_MULTIPLIER: Record<Importance, number> = {
  [-1]: 0.4,
  0: 1,
  1: 2.5,
};

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

/**
 * What a gap of N stops between two answers is worth, indexed by that gap.
 *
 * A stated table rather than a formula, because the shape wanted here isn't
 * one any tidy curve produces — it's generous at the near end and then falls
 * off a cliff:
 *
 *     0 stops   1.00   the same answer
 *     1 stop    0.90   as good as agreement — that much is two people reading
 *                      the same slider slightly differently, not a difference
 *     2 stops   0.70   a real difference, still comfortably compatible
 *     3 stops   0.40   half the scale; leaning opposite ways
 *     4+ stops  0.00   too far apart to be worth anything at all
 *
 * The cliff at four is the point of it. A linear rule paid out for answers
 * that are plainly opposed — two thirds of the scale apart still scored a
 * third — and enough of those averaged into a respectable-looking number.
 * Here they contribute nothing, so a high score has to be *earned* by
 * questions the two people actually land close on.
 */
export const SIMILARITY_BY_DISTANCE: readonly number[] = [1, 0.9, 0.7, 0.4, 0, 0, 0];

/**
 * How much two answers agree, 0..1.
 *
 * Purely a function of the distance between the two positions, so it is
 * symmetric and needs no knowledge of the question. Both at -3 scores exactly
 * as well as both at +3: agreeing that you hate something is as much a match as
 * agreeing that you love it.
 *
 * Rounds the gap before looking it up, so a value that arrived off-grid can't
 * fall between two entries or index past the end of the table.
 */
export function similarity(a: number, b: number): number {
  const distance = Math.round(Math.abs(a - b));
  return SIMILARITY_BY_DISTANCE[distance] ?? 0;
}

/**
 * How much this question counts for this pair, 0..1 before weights.
 *
 * Driven by whoever feels more strongly. Two people who both answered 0 are
 * telling us the question is irrelevant to both of them, so it drops out of
 * the score entirely rather than counting as a perfect agreement — otherwise
 * indifference would be the cheapest way to score 100%.
 */
export function intensity(a: number, b: number): number {
  return Math.max(Math.abs(a), Math.abs(b)) / SCALE_MAX;
}

/** Symmetric combination of the two sides' stated importance. */
export function importanceFactor(ia: Importance, ib: Importance): number {
  return Math.sqrt(IMPORTANCE_MULTIPLIER[ia] * IMPORTANCE_MULTIPLIER[ib]);
}

export interface AnswerPair {
  a: number;
  b: number;
  importanceA: Importance;
  importanceB: Importance;
  /** The question's own base weight, set in /admin. */
  weight: number;
}

/** The weight one answered-by-both question carries for this pair. */
export function pairWeight(p: AnswerPair): number {
  return (
    p.weight * importanceFactor(p.importanceA, p.importanceB) * intensity(p.a, p.b)
  );
}

export interface ScoreResult {
  /** Weighted mean similarity, 0..1, or null when nothing carried weight. */
  score: number | null;
  /** Questions both people answered. */
  overlap: number;
  /** Sum of pair weights — how much evidence the score rests on. */
  weight: number;
}

/**
 * Weighted mean agreement across a set of questions.
 *
 * This is the same function the Cypher in `scoring.ts` computes server-side;
 * it lives here so the client can show a breakdown without a round trip, and
 * so the tests can hold both implementations to the same numbers.
 */
export function scoreAnswers(pairs: AnswerPair[]): ScoreResult {
  let num = 0;
  let den = 0;
  for (const p of pairs) {
    const w = pairWeight(p);
    if (w === 0) continue;
    num += similarity(p.a, p.b) * w;
    den += w;
  }
  return {
    score: den > 0 ? num / den : null,
    overlap: pairs.length,
    weight: den,
  };
}

/**
 * Reads a number off the wire without JavaScript's coercion surprises.
 *
 * `Number(null)` is 0 and `Number(true)` is 1, so coercing blindly would turn
 * a null answer into an explicit "don't care" — which is a real answer that
 * carries weight against anyone who feels strongly. Only actual numbers and
 * numeric strings are accepted.
 */
function toNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Snaps anything arriving from a client onto a legal scale position. */
export function toScaleValue(v: unknown): ScaleValue | null {
  const n = toNumber(v);
  if (n === null) return null;
  const r = Math.round(n);
  if (r < SCALE_MIN || r > SCALE_MAX) return null;
  return r as ScaleValue;
}

/** Anything unrecognised falls back to normal importance rather than failing. */
export function toImportance(v: unknown): Importance {
  const n = toNumber(v);
  if (n === null) return 0;
  const r = Math.round(n);
  return r === -1 || r === 1 ? (r as Importance) : 0;
}
